<template>
  <div
    class="lp-datepicker"
    :class="[preview && 'lp-datepicker-preview']"
    :data-ua-unique-id="dataUaUniqueId"
  >
    <div class="lp-datepicker--header">
      <div
        v-if="yearSelector"
        class="lp-datepicker--header--year-selector ly-col ly-spacebetween-start"
      >
        <span v-if="labelTop" class="lp-input-label font-weight-medium">
          {{ labelTop }}
          <span v-if="!preview && required" class="text-blue">*</span>
        </span>
        <slot name="year-selector">
          <lp-number-input
            v-model="currentYearRef"
            class="ly-no-grow ly-shrink"
            compact
            format="integer"
            no-format
            @keyup="validateYear"
          />
        </slot>
      </div>
    </div>
    <div class="ly-even-4 ly-no-gap">
      <day-picker
        v-for="(month, index) in Array.from({ length: 12 })"
        :key="index"
        :page-date="getMonth(index)"
        :selected="selectedDates"
        :week-starts-on="weekStartsOn"
        :disabled-dates="disabledDates"
        :locale="locale"
        :weekday-format="weekdayFormat"
        :highlights="highlights"
        :data-ua-value="index"
        data-ua-multiple-id="ua-datepicker-daypicker"
        @select="selectDay"
      />
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  ref,
  toRef,
  computed,
  watchEffect,
  PropType,
} from 'vue'
import { parse, isValid, format, startOfMonth, isSameDay } from 'date-fns'
import DayPicker from './DayPicker.vue'
import LpSelect from '@/components/select/LpSelect.vue'
import LpNumberInput from '@/components/numberinput/LpNumberInput.vue'

const TIME_RESOLUTIONS = ['day', 'month', 'year']
type Highlight = {
  label: string
  date: Date
  color: string
}
export default defineComponent({
  name: 'lp-calendar',
  components: {
    LpNumberInput,
    LpSelect,
    DayPicker,
  },
  inheritAttrs: false,
  props: {
    status: { type: String, default: 'neutral' },
    message: { type: String, default: null },

    /**
     * `v-model` for selected date
     */
    modelValue: {
      required: false,
      type: [Date, Array] as PropType<Date | Date[]>,
      default: () => null,
    },
    /**
     * Dates not available for picking
     */
    disabledDates: {
      type: Object as PropType<{
        dates?: Date[]
        predicate?: (currentDate: Date) => boolean
      }>,
      required: false,
    },
    /**
     * Upper limit for available dates for picking
     */
    upperLimit: {
      type: Date as PropType<Date>,
      required: false,
    },
    /**
     * Lower limit for available dates for picking
     */
    lowerLimit: {
      type: Date as PropType<Date>,
      required: false,
    },
    /**
     * View on which the date picker should open. Can be either `year`, `month`, or `day`
     */
    startingView: {
      type: String as PropType<'year' | 'month' | 'day'>,
      required: false,
      default: 'day',
      validate: (v: unknown) =>
        typeof v === 'string' && TIME_RESOLUTIONS.includes(v),
    },
    /**
     * `date-fns`-type formatting for a month view heading
     */
    monthHeadingFormat: {
      type: String,
      required: false,
      default: 'LLLL yyyy',
    },
    /**
     * `date-fns`-type formatting for the month picker view
     */
    monthListFormat: {
      type: String,
      required: false,
      default: 'LLL',
    },
    /**
     * `date-fns`-type formatting for a line of weekdays on day view
     */
    weekdayFormat: {
      type: String,
      required: false,
      default: 'EE',
    },
    /**
     * `date-fns`-type format in which the string in the input should be both
     * parsed and displayed
     */
    inputFormat: {
      type: String,
      required: false,
      default: 'MM/dd/yy',
    },
    placeholder: {
      type: String,
      required: false,
      default: null,
    },
    /**
     * [`date-fns` locale object](https://date-fns.org/v2.16.1/docs/I18n#usage).
     * Used in string formatting (see default `monthHeadingFormat`)
     */
    locale: {
      type: Object as PropType<Locale>,
      required: false,
    },
    /**
     * Day on which the week should start.
     *
     * Number from 0 to 6, where 0 is Sunday and 6 is Saturday.
     * Week starts with a Monday (1) by default
     */
    weekStartsOn: {
      type: Number as PropType<1 | 2 | 3 | 4 | 5 | 6 | 0>,
      required: false,
      default: 0,
      validator: (value: any) => [0, 1, 2, 3, 4, 5, 6].includes(value),
    },
    /**
     * Disables datepicker and prevents it's opening
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Clears selected date
     */
    clearable: {
      type: Boolean,
      required: false,
      default: true,
    },
    /*
     * Allows user to input date manually
     */
    typeable: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If set, lower-level views won't show
     */
    minimumView: {
      type: String as PropType<'year' | 'month' | 'day'>,
      required: false,
      default: 'day',
      validate: (v: unknown) =>
        typeof v === 'string' && TIME_RESOLUTIONS.includes(v),
    },
    highlights: {
      type: Array as PropType<Highlight[]>,
      default: (): Highlight[] => [],
    },
    preview: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataUaUniqueId: {
      type: String,
      required: false,
      default: 'ua-datepicker-wrapper',
    },
    labelTop: {
      type: String,
      required: false,
      default: null,
    },
    required: {
      type: Boolean,
    },
    yearSelector: {
      type: Boolean,
      default: true,
    },
    currentYear: {
      type: Number,
      required: false,
      default: () => new Date().getFullYear(),
    },
  },
  emits: {
    'update:modelValue': (value: Date[] | Date | null | undefined) =>
      value === null ||
      value === undefined ||
      (Array.isArray(value) &&
        value.every((valueDate) => isValid(valueDate))) ||
      isValid(value),
    'update:currentYear': (value: number) => value > 0,
  },
  setup(props, { emit }) {
    const selectedDates = toRef<Date | Date[] | null>(props.modelValue)
    const viewShown = ref('none' as 'year' | 'month' | 'day' | 'none')
    const pageDate = ref<Date>(new Date())
    const inputRef = ref(null)
    const input = ref('')
    const originalMessage = ref(props.message)
    const messageValue = ref(props.message)

    const currentYearRef = ref<number>(props.currentYear)

    watchEffect(() => {
      if (inputRef.value) {
        const parsed = parse(input.value, props.inputFormat, new Date())
        if (isValid(parsed)) {
          pageDate.value = parsed
          messageValue.value = originalMessage.value
        } else {
          if (input.value.length > props.inputFormat.length) {
            input.value = ''
            emit('update:modelValue', null)
            messageValue.value = 'Date is not valid. Please try again.'
          }
        }
      }
    })

    watchEffect(
      () =>
        (input.value =
          props.modelValue && isValid(props.modelValue)
            ? format(props.modelValue, props.inputFormat, {
                locale: props.locale,
              })
            : ''),
    )
    watchEffect(() => {
      originalMessage.value = props.message
      messageValue.value = originalMessage.value
    })

    const renderView = (view: typeof viewShown.value = 'none') => {
      if (!props.disabled) {
        viewShown.value = view
        if (!props.modelValue) {
          pageDate.value = new Date()
        }
      }
    }
    watchEffect(() => {
      if (props.disabled) viewShown.value = 'none'
    })
    const selectYear = (date: Date) => {
      pageDate.value = date

      if (props.minimumView === 'year') {
        viewShown.value = 'none'
        emit('update:modelValue', date)
      } else {
        viewShown.value = 'month'
      }
    }
    const selectMonth = (date: Date) => {
      pageDate.value = date

      if (props.minimumView === 'month') {
        viewShown.value = 'none'
        emit('update:modelValue', date)
      } else {
        viewShown.value = 'day'
      }
    }
    const selectDay = (date: Date) => {
      let newModelValue: Date | Date[] | undefined | null = selectedDates.value
      if (
        Array.isArray(selectedDates.value) &&
        Array.isArray(props.modelValue) &&
        Array.isArray(newModelValue)
      ) {
        if (
          selectedDates.value.some((selectedDay) =>
            isSameDay(selectedDay, date),
          )
        ) {
          newModelValue = selectedDates.value.filter(
            (modelValueDate) => !isSameDay(modelValueDate, date),
          )
        } else {
          newModelValue.push(date)
        }
      } else {
        if (
          !newModelValue ||
          (newModelValue instanceof Date && !isSameDay(newModelValue, date))
        ) {
          newModelValue = [date]
        } else {
          newModelValue = null
        }
      }

      selectedDates.value = newModelValue
      emit('update:modelValue', newModelValue)

      viewShown.value = 'none'
    }
    const clearModelValue = () => {
      if (props.clearable) {
        emit('update:modelValue', null)
      }
    }

    const keyUp = (event: KeyboardEvent | MouseEvent) => {
      const code = event.code ? event.code : event.button
      // close calendar if escape or enter are pressed
      const closeButton = [
        27, // escape
        13, // enter
      ].includes(code as number)

      if (closeButton) {
        inputRef.value?.blur()
      }
      if (props.typeable && input.value.length === props.inputFormat.length) {
        const parsedDate = parse(input.value, props.inputFormat, new Date())
        if (isValid(parsedDate)) {
          emit('update:modelValue', parsedDate)
        }
      }
      renderView()
    }

    const initialView = computed(() => {
      const startingViewOrder = TIME_RESOLUTIONS.indexOf(props.startingView)
      const minimumViewOrder = TIME_RESOLUTIONS.indexOf(props.minimumView)

      return startingViewOrder < minimumViewOrder
        ? props.minimumView
        : props.startingView
    })

    const getMonth = (index: number) => {
      return new Date(currentYearRef.value, index)
    }

    const yearOptions = computed(() => {
      const lowerYear =
        props.lowerLimit && isValid(props.lowerLimit)
          ? props.lowerLimit.getFullYear()
          : new Date().getFullYear()
      const upperYear =
        props.upperLimit && isValid(props.upperLimit)
          ? props.upperLimit.getFullYear()
          : new Date().getFullYear()
      const numberOfYears = upperYear - lowerYear + 1

      return new Array(numberOfYears > 0 ? numberOfYears : 1)
        .fill(1)
        .map((_, i) => {
          const year = lowerYear + i

          return {
            name: year,
            value: year,
          }
        })
    })
    const validateYear = (e) => {
      if (currentYearRef.value.toString().length >= 4) {
        currentYearRef.value = isValid(
          parse(currentYearRef.value?.toString(), 'yyyy', new Date()),
        )
          ? currentYearRef.value
          : null
      }
    }

    return {
      input,
      inputRef,
      pageDate,
      renderView,
      selectYear,
      selectMonth,
      selectDay,
      keyUp,
      viewShown,
      clearModelValue,
      initialView,
      messageValue,
      startOfMonth,
      getMonth,
      selectedDates,
      yearOptions,
      currentYearRef,
      validateYear,
    }
  },
})
</script>
